'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CourseLesson } from '@/types/course';
import updateUserActivity from '@/lib/api/update-user-activity';

// Simplified course type for learning path navigation
interface LearningPathCourse {
  url: string;
  title?: string;
  uid?: string;
  chapters?: {
    lessons?: {
      lessons?: CourseLesson[];
    };
  }[];
}

interface UseAutoNextProps {
  lessons: CourseLesson[];
  currentLessonUrl: string; // e.g., "/lesson-slug"
  courseId: string;
  courseEntryUid: string;
  currentLessonEntryUid?: string;
  learningPath?: string; // Optional - for learning path routes
  learningPathCourses?: LearningPathCourse[]; // All courses in the learning path
  /** When false/omitted, completing the last course does not route to /assessment */
  learningPathHasAssessment?: boolean;
}

interface UseAutoNextReturn {
  currentIndex: number;
  nextLesson: CourseLesson | null;
  previousLesson: CourseLesson | null;
  isLastLesson: boolean;
  isFirstLesson: boolean;
  isLastCourseInPath: boolean;
  goToNextLesson: () => void;
  goToPreviousLesson: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
}

export function useAutoNext({
  lessons,
  currentLessonUrl,
  courseId,
  courseEntryUid,
  currentLessonEntryUid,
  learningPath,
  learningPathCourses,
  learningPathHasAssessment = false,
}: UseAutoNextProps): UseAutoNextReturn {
  const router = useRouter();
  const queryClient = useQueryClient();

  // Find current lesson index
  const currentIndex = useMemo(() => {
    return lessons.findIndex((lesson) => lesson.url === currentLessonUrl);
  }, [lessons, currentLessonUrl]);

  // Get next lesson
  const nextLesson = useMemo(() => {
    if (currentIndex === -1 || currentIndex >= lessons.length - 1) {
      return null;
    }
    return lessons[currentIndex + 1];
  }, [lessons, currentIndex]);

  // Get previous lesson
  const previousLesson = useMemo(() => {
    if (currentIndex <= 0) {
      return null;
    }
    return lessons[currentIndex - 1];
  }, [lessons, currentIndex]);

  // Find current course index in learning path
  const currentCourseIndex = useMemo(() => {
    if (!learningPathCourses || !learningPath) return -1;
    return learningPathCourses.findIndex((course) => course.url === `/${courseId}`);
  }, [learningPathCourses, learningPath, courseId]);

  // Get next course in learning path
  const nextCourse = useMemo(() => {
    if (!learningPathCourses || currentCourseIndex === -1) return null;
    if (currentCourseIndex >= learningPathCourses.length - 1) return null;
    return learningPathCourses[currentCourseIndex + 1];
  }, [learningPathCourses, currentCourseIndex]);

  // Get first lesson of next course
  const firstLessonOfNextCourse = useMemo(() => {
    if (!nextCourse) return null;
    const nextCourseLessons = nextCourse.chapters?.[0]?.lessons?.lessons;
    if (!nextCourseLessons || nextCourseLessons.length === 0) return null;
    return nextCourseLessons[0];
  }, [nextCourse]);

  const isLastLesson = currentIndex === lessons.length - 1;
  const isFirstLesson = currentIndex === 0;
  const isLastCourseInPath = learningPathCourses 
    ? currentCourseIndex === learningPathCourses.length - 1 
    : true;
  const hasNext = nextLesson !== null || (isLastLesson && nextCourse !== null);
  const hasPrevious = previousLesson !== null;

  // Build base path based on context (learning path or regular course)
  const getBasePath = useCallback(() => {
    return learningPath
      ? `/learning-paths/${learningPath}/${courseId}`
      : `/courses/${courseId}`;
  }, [learningPath, courseId]);

  // Navigate to next lesson
  const goToNextLesson = useCallback(() => {
    const basePath = getBasePath();

    // Navigate immediately
    if (nextLesson) {
      // Navigate to next lesson in current course
      router.push(`${basePath}${nextLesson.url}`);
    } else if (isLastLesson && learningPath) {
      // Last lesson of course in a learning path (or standalone course completed)
      if (nextCourse) {
        if (firstLessonOfNextCourse) {
          // Navigate to first lesson of next course
          const nextCourseUrl = nextCourse.url; // e.g., "/course-slug"
          router.push(`/learning-paths/${learningPath}${nextCourseUrl}${firstLessonOfNextCourse.url}`);
        } else {
          // Next course is standalone (no lessons) - go to its overview page
          const nextCourseUrl = nextCourse.url?.startsWith('/') ? nextCourse.url : `/${nextCourse.url}`;
          router.push(`/learning-paths/${learningPath}${nextCourseUrl}`);
        }
      } else if (isLastCourseInPath) {
        if (learningPathHasAssessment) {
          router.push(`/learning-paths/${learningPath}/assessment`);
        } else {
          router.push(`/learning-paths/${learningPath}`);
        }
      } else {
        // Fallback to learning path page
        router.push(`/learning-paths/${learningPath}`);
      }
    } else if (isLastLesson) {
      // Regular course completed - navigate back to course page
      router.push(basePath);
    }

    // Mark current lesson as complete in the background (non-blocking)
    if (currentLessonEntryUid) {
      updateUserActivity({
        isLessonVideo: true,
        lessonEntryUid: currentLessonEntryUid,
        lessonActivity: { progress: { isCompleted: true, percentage: 100 } },
        courseEntryUid,
        courseActivity: null,
        queryClient,
      }).catch(() => {
        // Silently fail - navigation already happened
      });
    }
  }, [
    nextLesson,
    isLastLesson,
    isLastCourseInPath,
    courseEntryUid,
    currentLessonEntryUid,
    router,
    getBasePath,
    learningPath,
    nextCourse,
    firstLessonOfNextCourse,
    learningPathHasAssessment,
    queryClient,
  ]);

  // Navigate to previous lesson
  const goToPreviousLesson = useCallback(() => {
    if (previousLesson) {
      const basePath = getBasePath();
      router.push(`${basePath}${previousLesson.url}`);
    }
  }, [previousLesson, router, getBasePath]);

  return {
    currentIndex,
    nextLesson,
    previousLesson,
    isLastLesson,
    isFirstLesson,
    isLastCourseInPath,
    goToNextLesson,
    goToPreviousLesson,
    hasNext,
    hasPrevious,
  };
}

export default useAutoNext;
